import React from 'react';
import { motion } from 'framer-motion';
import { Droplet, Flame, Sun, Grid3x3, Sparkles } from 'lucide-react';
import { TreatedImage } from './TreatedImage';
import { ReserveButton } from './ReserveButton';
const steps = [{
  number: '01',
  icon: Droplet,
  title: 'Injectable Collagen Biostimulation',
  description: 'Strategic collagen biostimulation initiates long-term collagen production and structural support.'
}, {
  number: '02',
  icon: Flame,
  title: 'Thermal Collagen Activation',
  description: 'Precision-controlled, high-intensity energy delivers controlled dermal heating, activating fibroblasts and stimulating collagen remodeling for improved firmness and elasticity.'
}, {
  number: '03',
  icon: Sun,
  title: '640 nm Cellular Activation',
  description: 'Pulsed 640 nm light energy activates regenerative cellular signaling, supporting healthier, more resilient skin.'
}, {
  number: '04',
  icon: Grid3x3,
  title: 'Fractional RF Remodeling',
  description: 'Radiofrequency energy remodels collagen at deeper levels and refines skin texture and quality.'
}, {
  number: '05',
  icon: Sparkles,
  title: 'Regenerative Infusion',
  description: 'Growth factors, peptides, and regenerative actives are delivered through precision microchannels to support repair, hydration, and skin vitality.'
}];
export function ProtocolSection() {
  return <section id="treatment" className="relative py-24 md:py-32 bg-bc-bg" data-id="element-133">
      <div className="container mx-auto px-6" data-id="element-134">
        <div className="max-w-4xl mx-auto" data-id="element-135">
          {/* Header with Dr Fiona */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center mb-20" data-id="element-136">
            <motion.div initial={{
            opacity: 0,
            y: 24
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-80px'
          }} transition={{
            duration: 0.7,
            ease: [0.22, 1, 0.36, 1]
          }} className="order-2 lg:order-1" data-id="element-137">
              <TreatedImage src="https://cdn.magicpatterns.com/uploads/wVs98VgUpX6JAXNTwrPt9V/Screenshot_2026-06-16_at_3.36.26_PM.png" alt="Dr Fiona Frie demonstrating a regenerative collagen device at Be CLINICAL" wrapperClassName="aspect-[4/5] w-full max-w-sm mx-auto lg:mx-0" data-id="element-138" />
            </motion.div>

            <div className="space-y-5 order-1 lg:order-2 text-center lg:text-left" data-id="element-139">
              <div className="text-bc-pink font-sans text-sm md:text-base tracking-wide2 uppercase" data-id="element-140">
                THE SCIENCE
              </div>
              <h2 className="font-display font-extrabold text-3xl md:text-4xl lg:text-5xl text-bc-bone" data-id="element-141">
                FIVE TECHNOLOGIES, ONE EXPERIENCE
              </h2>
              <p className="text-bc-bone-2 font-sans text-lg md:text-xl leading-relaxed md:leading-loose" data-id="element-142">
                Dr Fiona Frie will walk you through each step of the Collagen
                Reset — the regenerative collagen treatments behind it, and what
                they do for your skin.
              </p>
            </div>
          </div>

          {/* Steps */}
          <div className="relative space-y-8 md:space-y-12" data-id="element-143">
            {/* Connector line */}
            <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-px bg-bc-bone opacity-[0.08] -translate-x-1/2" data-id="element-144" />

            {steps.map((step, index) => {
            const Icon = step.icon;
            const isEven = index % 2 === 0;
            return <div key={step.number} className={`relative flex flex-col md:flex-row items-start gap-6 ${isEven ? 'md:flex-row' : 'md:flex-row-reverse'}`} data-id="element-145">
                  {/* Card */}
                  <div className="flex-1 group" data-id="element-146">
                    <div className="relative bg-bc-card border border-hairline rounded-bc p-6 md:p-8 transition-all bc-ease hover:border-bc-pink hover:bc-glow-box-soft" data-id="element-147">
                      {/* Large step number background */}
                      <div className="absolute top-4 right-4 text-6xl md:text-7xl font-sans font-bold text-bc-pink opacity-10" data-id="element-148">
                        {step.number}
                      </div>

                      {/* Icon */}
                      <div className="relative mb-4" data-id="element-149">
                        <Icon className="w-8 h-8 text-bc-pink" strokeWidth={1.5} data-id="element-150" />
                      </div>

                      {/* Content */}
                      <div className="relative space-y-3" data-id="element-151">
                        <h3 className="font-sans font-semibold text-xl md:text-2xl text-bc-bone" data-id="element-152">
                          {step.title}
                        </h3>
                        <p className="text-bc-bone-2 font-sans text-base md:text-lg leading-relaxed" data-id="element-153">
                          {step.description}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Node on connector line (desktop only) */}
                  <div className="hidden md:flex absolute left-1/2 top-8 -translate-x-1/2 w-3 h-3 rounded-full bg-bc-pink bc-glow-box-soft" data-id="element-154" />

                  {/* Spacer for alternating layout */}
                  <div className="hidden md:block flex-1" data-id="element-155" />
                </div>;
          })}
          </div>

          {/* Closing line */}
          <p className="text-center text-bc-bone-2 font-sans text-lg md:text-xl leading-relaxed mt-16 max-w-2xl mx-auto" data-id="element-156">
            The result is a comprehensive skin-regeneration experience available
            only at Be CLINICAL — and we can't wait to show you how it works.
          </p>

          <div className="mt-10 flex justify-center" data-id="element-157">
            <ReserveButton label="RSVP NOW" className="w-full sm:w-auto" data-id="element-158" />
          </div>
        </div>
      </div>
    </section>;
}